import React from 'react';
import { Loader, Modal } from '@mantine/core';
import { Button } from '../../../../components/Button';
import { useRestigoPushesController } from './useRestigoPushesController';
import classes from './restigo-pushes-modal.module.scss';

type RestigoPushesModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string | null;
};

const STATUS_LABEL: Record<string, string> = {
  success: 'נשלח',
  failed: 'נכשל',
  timeout: 'פסק זמן',
  error: 'שגיאה',
};

const formatWhen = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('he-IL');
};

export const RestigoPushesModal: React.FC<RestigoPushesModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
}) => {
  const { rows, isLoading, error, resend, resendingSessionId } = useRestigoPushesController({
    tenantId,
    employeeSession,
    opened,
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<span>{'שליחות רסטיגו'}</span>}
      size="lg"
      centered
      radius="md"
      zIndex={302}
      closeButtonProps={{ 'data-testid': 'restigo-pushes-close' } as Record<string, string>}
    >
      <div className={classes.stack} data-testid="restigo-pushes-modal">
        {error && <p className={classes.error}>{error}</p>}
        {isLoading ? (
          <div className={classes.center}>
            <Loader />
          </div>
        ) : rows.length === 0 ? (
          <p className={classes.empty}>{'אין שליחות רסטיגו אחרונות.'}</p>
        ) : (
          <div className={classes.list}>
            {rows.map((row) => (
              <div key={row.sessionId} className={classes.item} data-testid={`restigo-pushes-row-${row.sessionId}`}>
                <div className={classes.itemMain}>
                  <span className={classes.rowTitle}>{row.sessionName ?? `הפעלה ${row.sessionId}`}</span>
                  <span className={classes.rowMeta}>{formatWhen(row.lastAttemptAt)}</span>
                </div>
                <div className={classes.itemEnd}>
                  <span
                    className={`${classes.pill} ${row.status === 'success' ? classes.pillSuccess : classes.pillFailed}`}
                  >
                    {STATUS_LABEL[row.status] ?? row.status}
                  </span>
                  {row.status !== 'success' && (
                    <Button
                      variant="save"
                      size="sm"
                      onClick={() => resend(row.sessionId)}
                      disabled={resendingSessionId != null}
                      dataTestId={`restigo-pushes-resend-${row.sessionId}`}
                    >
                      {'שלח שוב'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};
